/** @jsxImportSource theme-ui */
import { Link, Flex, Text, Spinner } from 'theme-ui';
import { Table } from '@component-controls/components';
import { TrashIcon, PencilIcon } from '@heroicons/react/solid';
import { toast } from 'react-toastify';
import { get } from 'theme-ui';
import { useDeleteIncentiveMutation, useGetIncentivesQuery } from '@/store/apiService';
import Button from '@/components/Button';
import { useEffect, useState } from 'react';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { theme } from '../../theme';

type Props = {};

const tableColumns = [
    {
        columnKey: 'Active',
        Header: 'Active',
        accessor: 'Active',
        width: '50px',
        Cell: ({ cell }) => {
            const value = cell.value;
            return <>{value === true ? 'Yes' : 'No'}</>;
        },
    },
    {
        columnKey: 'Id',
        Header: 'Id',
        accessor: 'Id',
        width: '50px',
    },
    {
        columnKey: 'Heading',
        Header: 'Heading',
        accessor: 'Heading',
        width: '30%',
    },
    {
        columnKey: 'ActionId',
        Header: 'Action Id',
        accessor: 'ActionId',
    },
    {
        columnKey: 'InitialDelay',
        Header: 'Initial delay (seconds)',
        accessor: 'InitialDelayInSeconds',
    },
    {
        columnKey: 'InitialDeferredDays',
        Header: 'Delay after first login (days)',
        accessor: 'InitialDeferredDays',
    },
    {
        columnKey: 'ApplyToAllCompanies',
        Cell: ({ cell }) => {
            const value = cell.value;
            return <>{value === true ? 'Yes' : 'No'}</>;
        },
        Header: 'Apply to all companies',
        accessor: 'ApplyToAllCompanies',
    },
    {
        columnKey: 'Actions',
        Cell: ({ cell }) => {
            const row = cell.row;
            const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
            const [deleteIncentive, deleteIncentiveState] = useDeleteIncentiveMutation();

            useEffect(() => {
                if (deleteIncentiveState.error) {
                    // TODO: TODO: add proper types for error
                    // @ts-ignore
                    toast(deleteIncentiveState.error.error, {
                        autoClose: 1000,
                        bodyStyle: {
                            color: get(theme, 'colors.danger'),
                        },
                    });
                }
            }, [deleteIncentiveState]);

            return (
                <Flex
                    sx={{
                        justifyContent: 'flex-end',
                        flexGrow: 1,
                        alignItems: 'center',
                        a: { display: 'flex' },
                    }}
                >
                    {confirmationModalVisible ? (
                        <ConfirmationModal
                            hideOnClickOutside={true}
                            loading={deleteIncentiveState.isLoading}
                            headingText={`Delete incentive ${row.original.Id}`}
                            content={`Are you sure you want to delete it?`}
                            onNo={() => {
                                setConfirmationModalVisible(false);
                            }}
                            onYes={() => {
                                deleteIncentive(row.original.Id);
                            }}
                        />
                    ) : null}
                    <Link variant="button" href={`/#/incentives/${row.original.Id}`} title="Edit">
                        <PencilIcon width={16} height={16} />
                    </Link>

                    <Button
                        variant="basicDanger"
                        isLoading={deleteIncentiveState.isLoading}
                        onClick={() => {
                            setConfirmationModalVisible(true);
                        }}
                    >
                        <TrashIcon width={16} height={16} />
                    </Button>
                </Flex>
            );
        },
        accessor: 'Actions',
    },
];

const Incentives = (props: Props) => {
    const incentivesState = useGetIncentivesQuery({});
    const incentives = incentivesState.data?.Results ? incentivesState.data.Results : [];

    if (incentivesState.isLoading) {
        return <Spinner size={16} />;
    }

    return (
        <Flex>
            {incentives.length > 0 ? (
                <Table header={true} sorting={true} columns={tableColumns} data={incentives} />
            ) : (
                <Text>No incentives found</Text>
            )}
        </Flex>
    );
};

export default Incentives;
